import { useEffect, useState } from 'react';
import { CalendarDays, MapPin, UserRound } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { formatValue } from '@/lib/format';
import { logSwallowed } from '@/lib/errors';

// ============================================================================
// PatientProfileBlock — блок «Профиль учётной записи» в карточке пациента
// (только добавление, поставочные формы НЕ затрагиваются).
//
// Поля профиля (entities): ДВА адреса (текущий и проживание), пол, дата
// рождения — для ВСЕХ баз (ядро). У карточки пациента могут быть СВОИ
// одноимённые реквизиты; здесь показывается ПРОФИЛЬ (источник — учётная
// запись пациента, medic_patients.user_id).
//
// Адреса показываются ОБА (консультация address-resolution §5):
//   «Сейчас: …» — текущий адрес + источник/дата (вручную/по геолокации/по IP);
//   «Проживание: …» — адрес проживания; если текущий не указан, подпись —
//   «Проживание (текущий не указан)». Пустой адрес — «адрес не указан».
//
// Нечего показывать / нет прав / нет связи — блок не отрисовывается (тихий
// пропуск read-only докомплектации). Блоки скорой (PatientAmbulanceHistoryBlock,
// PatientCriticalBar) не затрагиваются — здесь свой источник данных.
// ============================================================================

interface ProfileFields {
    addressCurrent: string;
    addressHome: string;
    addressSource: string;
    addressUpdatedAt: string;
    genderLabel: string;
    birthDate: string;
}

const GENDER_LABELS: Record<string, string> = {
    male: 'Мужской',
    female: 'Женский',
};

// Подписи источников текущего адреса (SSoT кодов — сервер: manual|gps|ip).
const ADDRESS_SOURCE_LABELS: Record<string, string> = {
    manual: 'вручную',
    gps: 'по геолокации',
    ip: 'по IP',
};

function addressSourceLine(source: string, updatedAt: string): string {
    const parts: string[] = [];
    const label = ADDRESS_SOURCE_LABELS[source] || '';
    if (label) parts.push(label);
    if (updatedAt) {
        const date = formatValue(updatedAt, 'datetime:dd.MM.yyyy HH:mm', 'datetime');
        if (date) parts.push(date);
    }
    return parts.join(' · ');
}

function Row({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
    return (
        <div className="flex items-start gap-2 rounded-lg border bg-card px-3 py-2">
            <span className="mt-0.5 text-muted-foreground">{icon}</span>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="whitespace-pre-wrap break-words text-sm">{value}</div>
                {hint && <div className="mt-0.5 text-[10px] text-muted-foreground">{hint}</div>}
            </div>
        </div>
    );
}

export function PatientProfileBlock({ linkedUserId }: { linkedUserId: string }) {
    const [profile, setProfile] = useState<ProfileFields | null>(null);

    useEffect(() => {
        let alive = true;
        if (!linkedUserId) return;
        (async () => {
            try {
                const r: any = await getSDK().entity.getEntity({ id: linkedUserId });
                if (!alive) return;
                const fields: ProfileFields = {
                    addressCurrent: String(r?.addressCurrent || ''),
                    addressHome: String(r?.addressHome || ''),
                    addressSource: String(r?.addressCurrentSource || ''),
                    addressUpdatedAt: String(r?.addressCurrentUpdatedAt || ''),
                    genderLabel: GENDER_LABELS[String(r?.gender || '')] || '',
                    birthDate: String(r?.birthDate || ''),
                };
                if (fields.addressCurrent || fields.addressHome || fields.genderLabel || fields.birthDate) {
                    setProfile(fields);
                }
            } catch (e) {
                logSwallowed(e, 'components/medic/PatientProfileBlock.tsx');
            }
        })();
        return () => { alive = false; };
    }, [linkedUserId]);

    if (!profile) return null;

    const currentValue = profile.addressCurrent.trim() || 'адрес не указан';
    const homeValue = profile.addressHome.trim() || 'адрес не указан';
    // §5 консультации: если текущий адрес не указан — подпись проживания
    // явно говорит об этом (не путать с «адрес не указан» в значении).
    const homeLabel = profile.addressCurrent.trim() ? 'Проживание' : 'Проживание (текущий не указан)';
    const sourceLine = addressSourceLine(profile.addressSource, profile.addressUpdatedAt);

    const rows = [
        <Row
            key="address-current"
            icon={<MapPin className="h-4 w-4" />}
            label="Сейчас"
            value={currentValue}
            hint={sourceLine || undefined}
        />,
        <Row
            key="address-home"
            icon={<MapPin className="h-4 w-4" />}
            label={homeLabel}
            value={homeValue}
        />,
        profile.genderLabel
            ? <Row key="gender" icon={<UserRound className="h-4 w-4" />} label="Пол (профиль)" value={profile.genderLabel} />
            : null,
        profile.birthDate
            ? <Row
                key="birth"
                icon={<CalendarDays className="h-4 w-4" />}
                label="Дата рождения (профиль)"
                value={`${formatValue(profile.birthDate, '', 'date')} · ${profile.birthDate.slice(0, 4)} г.`}
              />
            : null,
    ].filter(Boolean);

    return (
        <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Профиль учётной записи</div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{rows}</div>
        </section>
    );
}
